/**
 * Nopparalli (decision 2026-09-27): the Nordic five-dice scoring game under an own name.
 * Five dice, up to three rolls per turn (dice can be kept between rolls), 15 categories that
 * each player fills exactly once. Upper section bonus 50 when the upper sum is at least 63.
 * Pure functions, shared by the server and the browser.
 */

export const DICE_COUNT = 5;
export const ROLLS_PER_TURN = 3;

export const UPPER = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes'] as const;
export const LOWER = [
  'pair',
  'twoPairs',
  'threeKind',
  'fourKind',
  'smallStraight',
  'largeStraight',
  'fullHouse',
  'chance',
  'jackpot',
] as const;
export const CATEGORIES = [...UPPER, ...LOWER] as const;
export type Category = (typeof CATEGORIES)[number];

export const UPPER_BONUS_THRESHOLD = 63;
export const UPPER_BONUS = 50;
export const JACKPOT_POINTS = 50;
export const SMALL_STRAIGHT_POINTS = 15;
export const LARGE_STRAIGHT_POINTS = 20;

/** Filled categories of one player (points, 0 = crossed out). */
export type ScoreSheet = Partial<Record<Category, number>>;

export type Dice = readonly number[];

export function isValidDice(dice: Dice): boolean {
  return dice.length === DICE_COUNT && dice.every((d) => Number.isInteger(d) && d >= 1 && d <= 6);
}

function counts(dice: Dice): number[] {
  const c = [0, 0, 0, 0, 0, 0, 0];
  for (const d of dice) c[d]!++;
  return c;
}

const sum = (dice: Dice) => dice.reduce((a, b) => a + b, 0);

/** Points the dice give in a category (0 if the dice do not qualify). */
export function scoreFor(category: Category, dice: Dice): number {
  const c = counts(dice);
  const upperIndex = (UPPER as readonly string[]).indexOf(category);
  if (upperIndex >= 0) return c[upperIndex + 1]! * (upperIndex + 1);

  // Faces from high to low, so the highest combination is chosen.
  const faces = [6, 5, 4, 3, 2, 1];
  switch (category) {
    case 'pair': {
      const f = faces.find((v) => c[v]! >= 2);
      return f ? f * 2 : 0;
    }
    case 'twoPairs': {
      const pairs = faces.filter((v) => c[v]! >= 2);
      return pairs.length >= 2 ? pairs[0]! * 2 + pairs[1]! * 2 : 0;
    }
    case 'threeKind': {
      const f = faces.find((v) => c[v]! >= 3);
      return f ? f * 3 : 0;
    }
    case 'fourKind': {
      const f = faces.find((v) => c[v]! >= 4);
      return f ? f * 4 : 0;
    }
    case 'smallStraight':
      return [1, 2, 3, 4, 5].every((v) => c[v] === 1) ? SMALL_STRAIGHT_POINTS : 0;
    case 'largeStraight':
      return [2, 3, 4, 5, 6].every((v) => c[v] === 1) ? LARGE_STRAIGHT_POINTS : 0;
    case 'fullHouse': {
      const three = faces.find((v) => c[v] === 3);
      const two = faces.find((v) => c[v] === 2);
      return three && two ? sum(dice) : 0;
    }
    case 'chance':
      return sum(dice);
    case 'jackpot':
      return faces.some((v) => c[v] === 5) ? JACKPOT_POINTS : 0;
  }
  return 0;
}

export function upperSum(sheet: ScoreSheet): number {
  return UPPER.reduce((a, cat) => a + (sheet[cat] ?? 0), 0);
}

export function upperBonus(sheet: ScoreSheet): number {
  return upperSum(sheet) >= UPPER_BONUS_THRESHOLD ? UPPER_BONUS : 0;
}

export function totalScore(sheet: ScoreSheet): number {
  return CATEGORIES.reduce((a, cat) => a + (sheet[cat] ?? 0), 0) + upperBonus(sheet);
}

export function openCategories(sheet: ScoreSheet): Category[] {
  return CATEGORIES.filter((c) => sheet[c] === undefined);
}

export function isSheetComplete(sheet: ScoreSheet): boolean {
  return openCategories(sheet).length === 0;
}
